import { apiClient } from "./authService";

/* ------------------------------------------------------------------ types */

export type MatchStatus = "OPEN" | "FULL" | "CLOSED" | "CANCELLED";
export type MatchMode = "PUBLIC" | "PRIVATE";
export type MatchWhen = "upcoming" | "past" | "all";
export type MatchRequestStatus =
  | "PENDING"
  | "ACCEPTED"
  | "DECLINED"
  | "CANCELLED"
  | "REMOVED";

export const MATCH_STATUSES: MatchStatus[] = ["OPEN", "FULL", "CLOSED", "CANCELLED"];

export interface MatchParticipant {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  role: "HOST" | "PLAYER";
  /** null for the host. */
  joinedVia: "MATCH" | "INVITE" | null;
  joinedAt: string | null;
}

export interface Match {
  id: string;
  appointment_id: string;
  owner_user_id: string;
  players_needed: number;
  description: string | null;
  /** Category name, plain text — not a level id. */
  level: string | null;
  note: string | null;
  mode: MatchMode;
  status: MatchStatus;
  created_at: string;
  updated_at: string;
  appointment: {
    id: string;
    client_id: string;
    start_at: string;
    end_at: string;
    status: string;
    payment_status: string;
    invoice_status: string;
    total_price: number | string;
    service: { id: string; name: string } | null;
    unit: {
      id: string;
      name: string;
      short_name: string | null;
      business: { id: string; name: string };
    };
  };
  owner: { id: string; full_name: string; phone: string | null };
  currentPlayers: number;
  targetPlayers: number;
  playersNeeded: number;
  /** Lowest players_needed allowed: players already in, minus the host. */
  minPlayersNeeded: number;
  /** Highest players_needed allowed: the venue cap minus the host. */
  maxPlayersNeeded: number;
  full: boolean;
  bookingPaid: boolean;
  bookingCancelled: boolean;
  pendingRequestsCount: number;
  participants: MatchParticipant[];
}

export interface MatchRequest {
  id: string;
  status: MatchRequestStatus;
  message: string | null;
  responseReason: string | null;
  requester: { id: string; name: string; phone: string | null; email: string | null };
  created_at: string;
  responded_at: string | null;
}

export interface MatchDetails extends Match {
  requests: MatchRequest[];
}

export interface MatchesPage {
  items: Match[];
  total: number;
  page: number;
  size: number;
}

export interface MatchListParams {
  status?: MatchStatus[];
  mode?: MatchMode;
  when?: MatchWhen;
  businessId?: string;
  search?: string;
  /** 0-based. */
  page?: number;
  /** Max 200. */
  size?: number;
}

export interface MatchUpdatePayload {
  description?: string | null;
  level?: string | null;
  mode?: MatchMode;
  /** Within minPlayersNeeded..maxPlayersNeeded; past and closed matches too. */
  playersNeeded?: number;
}

export interface MatchDeleteResult {
  deleted: true;
  matchmakingId: string;
  appointmentId: string;
  removedPlayerUserIds: string[];
}

export interface MatchLevel {
  id: string;
  name: string;
  sortOrder: number;
}

/** Whether the levels list is known. "(removed)" is only meaningful once loaded. */
export type MatchLevelsStatus = "loading" | "loaded" | "failed";

export const MATCH_LEVEL_NAME_MAX = 50;
export const MATCH_DESCRIPTION_MAX = 500;

/* ---------------------------------------------------------------- matches */

const BASE = "/admin/matchmaking";

export const listMatches = async (
  params: MatchListParams
): Promise<MatchesPage> => {
  const { data } = await apiClient.get<MatchesPage>(BASE, {
    params: {
      ...(params.status?.length ? { status: params.status.join(",") } : {}),
      ...(params.mode ? { mode: params.mode } : {}),
      ...(params.when && params.when !== "all" ? { when: params.when } : {}),
      ...(params.businessId ? { businessId: params.businessId } : {}),
      ...(params.search ? { search: params.search } : {}),
      page: params.page ?? 0,
      size: params.size ?? 20,
    },
  });
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    total: Number(data?.total) || 0,
    page: Number(data?.page) || 0,
    size: Number(data?.size) || params.size || 20,
  };
};

export const getMatch = async (id: string): Promise<MatchDetails> => {
  const { data } = await apiClient.get<MatchDetails>(`${BASE}/${id}`);
  return data;
};

/**
 * Send only the fields that changed. A 400 means an unknown level or mode, or
 * a playersNeeded out of range or below the players already in.
 */
export const updateMatch = async (
  id: string,
  payload: MatchUpdatePayload
): Promise<MatchDetails> => {
  const { data } = await apiClient.patch<MatchDetails>(`${BASE}/${id}`, payload);
  return data;
};

export const closeMatch = async (id: string): Promise<MatchDetails> => {
  const { data } = await apiClient.post<MatchDetails>(`${BASE}/${id}/close`);
  return data;
};

/** Refused with 400 once the booking is paid or cancelled. */
export const removeMatchPlayer = async (
  id: string,
  playerUserId: string
): Promise<MatchDetails> => {
  const { data } = await apiClient.post<MatchDetails>(
    `${BASE}/${id}/players/${playerUserId}/remove`
  );
  return data;
};

/**
 * Drops the matchmaking but keeps the booking with the host. Players who joined
 * through the match are removed and notified; invite-link players stay.
 */
export const deleteMatch = async (id: string): Promise<MatchDeleteResult> => {
  const { data } = await apiClient.delete<MatchDeleteResult>(`${BASE}/${id}`);
  return data;
};

/* ----------------------------------------------------------------- levels */

// Every levels call answers with the full list, so callers replace state with it.
const unwrapLevels = (data: { items?: MatchLevel[] } | undefined) =>
  (Array.isArray(data?.items) ? data!.items : [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);

export const listMatchLevels = async (): Promise<MatchLevel[]> => {
  const { data } = await apiClient.get<{ items: MatchLevel[] }>(`${BASE}/levels`);
  return unwrapLevels(data);
};

// Create and rename show 400/409 under the input instead of as a toast.
export const createMatchLevel = async (body: {
  name: string;
  sortOrder?: number;
}): Promise<MatchLevel[]> => {
  const { data } = await apiClient.post<{ items: MatchLevel[] }>(
    `${BASE}/levels`,
    body,
    { suppressToast: true } as any
  );
  return unwrapLevels(data);
};

export const updateMatchLevel = async (
  levelId: string,
  body: { name?: string; sortOrder?: number }
): Promise<MatchLevel[]> => {
  const { data } = await apiClient.patch<{ items: MatchLevel[] }>(
    `${BASE}/levels/${levelId}`,
    body,
    { suppressToast: true } as any
  );
  return unwrapLevels(data);
};

export const reorderMatchLevels = async (ids: string[]): Promise<MatchLevel[]> => {
  const { data } = await apiClient.post<{ items: MatchLevel[] }>(
    `${BASE}/levels/reorder`,
    { ids }
  );
  return unwrapLevels(data);
};

export const deleteMatchLevel = async (levelId: string): Promise<MatchLevel[]> => {
  const { data } = await apiClient.delete<{ items: MatchLevel[] }>(
    `${BASE}/levels/${levelId}`
  );
  return unwrapLevels(data);
};
